import {
	CalloutCompat as Callout,
	CheckboxVisibility,
	ConstrainMode,
	DetailsListCompat as DetailsList,
	IDetailsList,
	IDetailsListProps,
	IDetailsListStyles,
	ISelection,
	MarqueeSelectionCompat as MarqueeSelection,
	PivotCompat as Pivot,
	PivotItem,
	SelectionMode,
	SpinnerCompat as Spinner,
	SpinnerSize,
	StackCompat as Stack,
	useTheme,
} from 'components/FluentUICompat';
import {useBoolean} from '@fluentui/react-hooks';
import {getRendererForHeaderWithTooltips} from '../../features/localizedTooltips/componentsWithTooltips/HeaderWithTooltipsAndStickySupport';
import {ColumnWithTooltipOptions} from 'features/localizedTooltips';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useCommand, useDocumentTitle, useSelection} from 'hooks';
import {ExcelExportButton} from './Excel/ExcelExportButton';
import {useTranslation} from 'react-i18next';
import {InfinityListFilterBubbles} from './InfinityListFilterBubbles';
import {InfinityListFilterPanel} from './InfinityListFilter';
import {useEntityContext} from 'components/EntityPage/EntityContext';
import {
	getRenderRowWithContextualMenu,
	GetRowStyles,
} from 'components/EntityList/RowWithContextualMenu/RowWithContextualMenu';
import {FilterOption} from 'types';
import {AdvancedFilterTreeNode} from './InfinityListAdvancedFilter';
import {Buffer} from 'buffer';

export type InfinityListColumn = ColumnWithTooltipOptions & {
	sortable?: boolean;
	filterable?: boolean;
	hidden?: boolean;
	filterOnFilter?: boolean;
	filterType?: string;
	hiddenAlways?: boolean;
};

export type InfinityListView = {
	headerText: string;
	accessKey: string;
	query: string;
};

export type InfinityListColumnFilterOptions = {
	key: string;
	fieldValuesUnique: FilterOption[];
};

export type InfinityListColumnMetaData = {
	key: string;
	mongoDBFn: (filter: InfinityListFilter) => string;
	linkName?: string;
	sortInverted?: boolean;
};

export type InfinityListProps = Omit<IDetailsListProps, 'columns'> & {
	pageName: string;
	tableId?: string;
	fixedHeight?: string | number;
	groupByFieldName?: string;
	initialSortField: string;
	initialSortOrder: string;
	initialView: string;
	keyPrefix: string;
	listId?: string;
	numberOfElements: number;
	sticky?: boolean;
	showHeader?: boolean;
	stillLoading: boolean;
	advancedFilterShow?: boolean;
	initalFilters: InfinityListFilter[];
	columns: InfinityListColumn[];
	columnMetaData: InfinityListColumnMetaData[];
	columnFilterOptions?: InfinityListColumnFilterOptions[];
	views: InfinityListView[];
	getRowStyles?: GetRowStyles;
	firstRender: () => void;
	loadItemsNext: (
		currentId: string,
		sortField: string,
		sortOrder: string,
	) => void;
	excelExport?: (sortField: string, sortOrder: string) => Promise<void>;
	applyFilter: (
		filters: InfinityListFilter[],
		sortField: string,
		sortOrder: string,
		currentView?: string,
		preFilteredIds?: string[],
		advancedFilterOn?: boolean,
		advancedFilterNodeList?: AdvancedFilterTreeNode[],
	) => void;
	applySort: (
		sortField: string,
		sortOrder: string | undefined,
		setSortOrder: (sortOrder: string) => void,
	) => void;
	handleInvokeItem: (item: any) => void;
	createHref?: (item: unknown) => string;
};

export type InfinityListFilter = {
	columnKey: string;
	selection: string;
	display: string;
	operation: string;
};

export interface InfinityListURLParams {
	[key: string]: any;
}

export type InfinityListItemWithId = {
	id: string;
};

export const InfinityList: React.FC<InfinityListProps> = ({
	initialSortField,
	initialSortOrder,
	stillLoading,
	sticky,
	numberOfElements,
	items,
	columns,
	initalFilters,
	firstRender,
	loadItemsNext,
	applyFilter,
	applySort,
	handleInvokeItem,
	createHref,
	getRowStyles,
	...props
}) => {
	// Translation
	const {t} = useTranslation('components/infinitylist');
	// ------------------------------------------------------------------------------------------------------

	//list pages get their tab title from the name they already render.
	useDocumentTitle([props.pageName]);

	// States to hold filters, sort field and order
	const [sortField, setSortField] = useState(initialSortField ?? '');
	const [sortOrder, setSortOrder] = useState(initialSortOrder ?? 'ASC');
	// The appliedFiltersr only hold the filters after user applies them
	const [appliedFilters, setAppliedFilters] = useState<InfinityListFilter[]>([
		...initalFilters,
	]);
	// The allListFilters holds also the filters the user is currently selecting on the panel
	const [allListFilters, setAllListFilters] = useState<InfinityListFilter[]>([
		...initalFilters,
	]);
	// ------------------------------------------------------------------------------------------------------

	// Keeps the loading spinner visible for the whole export, not only while a batch request is in flight
	const [isExporting, setIsExporting] = useState(false);
	const excelExportRef = useRef(props.excelExport);
	excelExportRef.current = props.excelExport;
	const excelExportWithSpinner = useCallback(
		async (sortField: string, sortOrder: string) => {
			setIsExporting(true);
			try {
				await excelExportRef.current?.(sortField, sortOrder);
			} finally {
				setIsExporting(false);
			}
		},
		[],
	);

	// The listRef helps to scroll to top when conditions are met
	const listRef = useRef<IDetailsList>(undefined);

	// The context allows selected items to be given to all neighbours of this list on same page.
	const {setSelectedItems} = useEntityContext<any>();

	const [selection] = useSelection<any>({
		onSelectionChanged(selectedItems) {
			setSelectedItems?.(selectedItems);
		},
	});
	// ------------------------------------------------------------------------------------------------------

	// The data of the list will be lazy loaded for the first time
	useEffect(() => {
		firstRender();
	}, []);
	useEffect(() => {
		if (listRef && listRef.current) {
			listRef.current?.scrollToIndex(0);
		}
	}, [listRef]);
	// ------------------------------------------------------------------------------------------------------

	// use Command for Excel
	useCommand(
		{
			key: `infinitylist-excel-${props.listId ?? props.tableId ?? 'default'}`,
			farCommand: true,
			priority: 0,
			hidden: props.excelExport === undefined,
			commandBarButtonAs(): React.ReactElement {
				return (
					<ExcelExportButton
						excelExport={excelExportWithSpinner}
						sortField={sortField}
						sortOrder={sortOrder}
					/>
				);
			},
		},
		[props.excelExport === undefined, excelExportWithSpinner, sortField, sortOrder],
	);
	// ------------------------------------------------------------------------------------------------------

	// The next batch of data will be lazy loaded as soon as the last row ist mounted
	const onRowDidMount = useCallback(
		(item: any) => {
			loadItemsNext(item.id, sortField, sortOrder);
		},
		[loadItemsNext, sortField, sortOrder],
	);
	// Handles when user clicks an item on the list
	const onItemInvoked = useCallback(
		(item: any) => {
			handleInvokeItem(item);
		},
		[handleInvokeItem],
	);
	// Allows the list header to stick on the top of page regardless of scroll
	const onRenderDetailsHeader = useCallback(
		() => getRendererForHeaderWithTooltips(sticky),
		[sticky],
	);
	// Adds context menu to each item and allows to open it in new tab
	const onRenderRow = useMemo(
		() => getRenderRowWithContextualMenu(createHref!, getRowStyles),
		[createHref, getRowStyles],
	);
	// Very important function that makes sure all list items are remounted other the onRowDidMount is not triggered at all
	const getKey = useCallback(
		(item: InfinityListItemWithId) => {
			return props.keyPrefix + item.id;
		},
		[props.keyPrefix],
	);
	// The user clicks on a column header in order to trigger sort
	const onColumnClick = useCallback(
		(
			_event: React.MouseEvent<HTMLElement>,
			column: InfinityListColumn,
		): void => {
			if (column.isSorted) {
				const sortOrderNew = column.isSortedDescending ? 'ASC' : 'DESC';
				applySort(column.key, sortOrderNew, setSortOrder);
				listRef.current?.scrollToIndex(0);
			} else if (column.sortable) {
				setSortField(column.key);
				applySort(column.key, undefined, setSortOrder);
				listRef.current?.scrollToIndex(0);
			}
		},
		[applySort, listRef.current, sortField, sortOrder],
	);
	// ------------------------------------------------------------------------------------------------------

	// Filter outs always hidden fields and add onColumnClick that is not present in parent component
	const columnsInfinity = useMemo(
		() =>
			columns
				.filter(x => x.hiddenAlways !== true)
				.map(x => {
					return {
						...x,
						onColumnClick,
						isSorted: sortField === x.key,
						isSortedDescending: sortOrder === 'DESC',
						showSortIconWhenUnsorted: x.sortable,
					};
				}),
		[columns, onColumnClick],
	);
	// Memorized detail list of fluent ui, that rerenders only when its props changes
	const compDetailList = useMemo(
		() => (
			<DetailsList
				items={items}
				onRowDidMount={onRowDidMount}
				onItemInvoked={onItemInvoked}
				onRenderDetailsHeader={onRenderDetailsHeader()}
				onRenderRow={onRenderRow}
				getKey={getKey}
				columns={columnsInfinity}
				selection={selection}
				useReducedRowRenderer={true}
				styles={
					props.fixedHeight
						? getFixedHeightListStyles(props.fixedHeight)
						: props.styles
				}
				checkboxVisibility={
					props.checkboxVisibility ??
					props.selectionMode === SelectionMode.multiple
						? CheckboxVisibility.always
						: CheckboxVisibility.onHover
				}
				constrainMode={props.constrainMode || ConstrainMode.unconstrained}
				selectionMode={props.selectionMode}
			/>
		),
		[
			items,
			onRowDidMount,
			onItemInvoked,
			onRenderDetailsHeader,
			onRenderRow,
			getKey,
			columnsInfinity,
			selection,
			props.selectionMode,
			props.fixedHeight,
			props.styles,
			props.checkboxVisibility,
			props.constrainMode,
		],
	);
	// Add selection to the memorized list
	const compDetailListWSelection = useMemo(
		() => (
			<MarqueeSelection
				selection={props.selection as ISelection}
				isDraggingConstrainedToRoot={true}
				isEnabled={props.selectionMode === SelectionMode.multiple}
			>
				{compDetailList}
			</MarqueeSelection>
		),
		[compDetailList, props.selection, props.selectionMode],
	);
	// ------------------------------------------------------------------------------------------------------

	// Memorized display of the results
	const compResults = useMemo(
		() =>
			numberOfElements > 0 ? compDetailListWSelection : <p>{t('NoResult')}</p>,
		[numberOfElements, compDetailListWSelection],
	);
	// Memorized header of list with views, filter bubbles and filter panel as well each filter item
	const compHeader = useMemo(
		() => (
			<InfinityListHeader
				columns={columns}
				sortField={sortField}
				sortOrder={sortOrder}
				appliedFilters={appliedFilters}
				allListFilters={allListFilters}
				listRef={listRef}
				pageName={props.pageName}
				views={props.views}
				showHeader={props.showHeader}
				initialView={props.initialView ?? 'STD'}
				stillLoading={stillLoading}
				columnFilterOptions={props.columnFilterOptions}
				setAppliedFilters={setAppliedFilters}
				setAllListFilters={setAllListFilters}
				applyFilter={applyFilter}
				advancedFilterShow={props.advancedFilterShow}
			/>
		),
		[
			columns,
			sortField,
			sortOrder,
			stillLoading,
			appliedFilters,
			allListFilters,
			listRef,
			props.pageName,
			props.views,
			props.showHeader,
			props.initialView,
			props.columnFilterOptions,
		],
	);

	return (
		<div style={{marginLeft: 20, marginTop: 20}}>
			<InfinityListCallout stillLoading={stillLoading || isExporting} />
			<InfinityListNumberElementsCommand numberOfElements={numberOfElements} />
			{compHeader}
			{compResults}
		</div>
	);
};

type InfinityListCalloutProps = {
	stillLoading: boolean;
};

const InfinityListCallout: React.FC<InfinityListCalloutProps> = ({
	stillLoading,
}) => {
	const {t} = useTranslation('components/infinitylist');
	const calloutTarget = useMemo(
		() => ({left: window.innerWidth - 20, top: window.innerHeight / 4}),
		[],
	);

	if (!stillLoading) {
		return null;
	}

	return (
		<Callout
			role='alertdialog'
			target={calloutTarget} // Place it at the right side
			hidden={false}
			isBeakVisible={false}
		>
			<div style={{padding: 20}}>
				{<Spinner size={SpinnerSize.medium} />}
				<p>{t('Loading')}</p>
			</div>
		</Callout>
	);
};

type InfinityListNumberElementsCommandProps = {
	numberOfElements: number;
};

const InfinityListNumberElementsCommand: React.FC<
	InfinityListNumberElementsCommandProps
> = ({numberOfElements}) => {
	const {t} = useTranslation('components/infinitylist');
	const theme = useTheme();
	useCommand(
		{
			key: 'infinitylist-number-of-elements',
			farCommand: true,
			priority: 1000,
			commandBarButtonAs(): React.ReactElement {
				return (
					<div
						style={{
							paddingTop: 12,
							paddingLeft: 20,
							background: theme.palette.neutralLight,
							width: 105,
						}}
					>
						{numberOfElements ?? 0}&nbsp;{t('Elements')}
					</div>
				);
			},
		},
		[numberOfElements],
	);
	return <></>;
};

type InfinityListHeaderProps = {
	pageName: string;
	views: InfinityListView[];
	showHeader?: boolean;
	sortField: string;
	sortOrder: string;
	initialView: string;
	stillLoading: boolean;
	advancedFilterShow?: boolean;
	columns: InfinityListColumn[];
	columnFilterOptions?: InfinityListColumnFilterOptions[];
	allListFilters: InfinityListFilter[];
	appliedFilters: InfinityListFilter[];
	listRef: React.MutableRefObject<IDetailsList | undefined>;
	setAppliedFilters: (appliedFilters: InfinityListFilter[]) => void;
	setAllListFilters: (allListFilters: InfinityListFilter[]) => void;
	applyFilter: (
		filters: InfinityListFilter[],
		sortField: string,
		sortOrder: string,
		currentView?: string,
		preFilteredIds?: string[],
		advancedFilterOn?: boolean,
		advancedFilterNodeList?: AdvancedFilterTreeNode[],
	) => void;
};

const InfinityListHeader: React.FC<InfinityListHeaderProps> = ({
	pageName,
	views,
	showHeader,
	sortField,
	sortOrder,
	appliedFilters,
	applyFilter,
	...props
}) => {
	const [currentViewKey, setCurrentViewKey] = useState<string | undefined>(
		props.initialView,
	);

	const [isPanelOpened, {setTrue: openPanel, setFalse: dismissPanel}] =
		useBoolean(false);

	const onLinkClick = (item?: any) => {
		setCurrentViewKey(item?.props.accessKey);
		applyFilter(appliedFilters, sortField, sortOrder, item?.props.accessKey);
	};

	return (
		<>
			{showHeader !== false && (
				<Stack horizontal style={{paddingBottom: 10}}>
					<Stack.Item>
						<div style={{paddingTop: 15}}>
							<b>{pageName}</b>
						</div>
					</Stack.Item>
					<Stack.Item>
						<Stack horizontal>
							<Pivot
								aria-label='Views'
								onLinkClick={onLinkClick}
								defaultSelectedKey={props.initialView}
								style={{
									position: 'fixed',
									right: 0,
									paddingRight: 20,
								}}
							>
								{views.map((v, i) => (
									<PivotItem
										key={'view' + i}
										headerText={v.headerText}
										accessKey={v.accessKey}
										itemKey={v.accessKey}
										itemIcon={
											currentViewKey === v.accessKey ? 'List' : undefined
										}
									></PivotItem>
								))}
							</Pivot>
						</Stack>
					</Stack.Item>
				</Stack>
			)}
			<InfinityListFilterPanel
				isPanelOpened={isPanelOpened}
				openPanel={openPanel}
				dismissPanel={dismissPanel}
				applyFilter={applyFilter}
				appliedFilters={appliedFilters}
				sortOrder={sortOrder}
				sortField={sortField}
				currentView={currentViewKey ?? ''}
				{...props}
			/>
			<InfinityListFilterBubbles
				sortField={sortField}
				sortOrder={sortOrder}
				currentView={currentViewKey ?? ''}
				appliedFilters={appliedFilters}
				applyFilter={applyFilter}
				{...props}
			/>
		</>
	);
};

const getFixedHeightListStyles: (
	height: string | number,
) => Partial<IDetailsListStyles> = height => ({
	root: {
		overflowX: 'scroll',
		selectors: {
			'& [role=grid]': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'start',
				height,
			},
		},
	},
	headerWrapper: {
		flex: '0 0 auto',
	},
	contentWrapper: {
		flex: '1 1 auto',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
});

export const funcDecodeHex24ToDocId: (
	base64String: string,
) => string = base64String => {
	const decoded = Buffer.from(base64String, 'base64').toString('utf-8');
	const delimiter = decoded?.includes('\n') ? '\nd' : ':';
	return decoded?.split(delimiter)[1] ?? '';
};
export const funcDecodeHex24ToEntity: (
	base64String: string,
) => string = base64String => {
	const decoded = Buffer.from(base64String, 'base64').toString('utf-8');
	const delimiter = decoded?.includes('\n') ? '\nd' : ':';
	return decoded?.split(delimiter)[0] ?? '';
};
